import { z } from 'zod';
import { UserError } from '../../errors';
import { type Tool, type ToolCallDetails, tool } from '../../tool';
import { withSandboxSpan } from '../runtime/spans';
import type { ExecForegroundArgs } from '../session';
import { formatExecResponse } from '../shared/output';
import {
  Capability,
  type ConfigureCapabilityTools,
  requireBoundSession,
} from './base';

const DEFAULT_BASH_TIMEOUT_MS = 120_000;
const MAX_BASH_TIMEOUT_MS = 600_000;
const DEFAULT_BASH_OUTPUT_PREVIEW_BYTES = 30_000;
const MAX_BASH_OUTPUT_PREVIEW_BYTES = 150_000;

const DETACHED_COMMANDS = new Set([
  'disown',
  'nohup',
  'screen',
  'setsid',
  'tmux',
]);

// const SHELL_INSTRUCTIONS = [
//   'When using Bash:',
//   '- Use the `Bash` tool to run a foreground bash command and wait for its result.',
//   '- The working directory is the current SandboxAgent workspace root. It does not persist `cd` across calls; use absolute paths or `cd path && ...` within one command.',
//   '- Shell state does not persist across calls, including aliases, variables, functions, and traps.',
//   '- Do not run interactive, detached, or background commands. Avoid `&`, `nohup`, `setsid`, `disown`, `tmux`, and `screen`.',
//   `- Use the optional timeout in milliseconds for longer foreground commands. The default is ${DEFAULT_BASH_TIMEOUT_MS}; the maximum is ${MAX_BASH_TIMEOUT_MS}.`,
//   '- When combining commands, prefer `&&` so failures stop subsequent work. Use `;` only when earlier failures are acceptable.',
//   '- Quote paths that contain spaces.',
//   '- Prefer `rg` and `rg --files` for text/file discovery when available.',
// ].join('\n');
const SHELL_INSTRUCTIONS = [
  '使用 Bash 时:',
  '- 使用 `Bash` 工具运行一个前台 bash 命令并等待其结果',
  '- 工作目录是当前工作区的根目录。它不会在多次调用之间保持 `cd` 的状态；请使用绝对路径，或在一条命令内使用 `cd path && ...`',
  '- Shell 状态不会在多次调用之间保留，包括别名、变量、函数和陷阱',
  '- 不要运行交互式、分离式或后台命令。避免使用 `&`、`nohup`、`setsid`、`disown`、`tmux` 和 `screen`',
  `对于耗时较长的前台命令，可使用可选的毫秒级超时。默认值为 ${DEFAULT_BASH_TIMEOUT_MS}；最大值为 ${MAX_BASH_TIMEOUT_MS}`,
  '- 组合命令时，优先使用 `&&`，这样前面的失败会阻止后续工作。只有当可以接受前面命令失败时，才使用 `;`',
  '- 用引号括起包含空格的路径',
  '- 当可用时，优先使用 `rg` 和 `rg --files` 进行文本/文件搜索',
].join('\n');

export type ShellArgs = {
  configureTools?: ConfigureCapabilityTools;
  outputPreviewBytes?: number;
};

class ShellCapability extends Capability {
  readonly type = 'shell';
  private readonly configureTools?: ConfigureCapabilityTools;
  private readonly outputPreviewBytes: number;

  constructor(args: ShellArgs = {}) {
    super();
    this.configureTools = args.configureTools;
    this.outputPreviewBytes = normalizeOutputPreviewBytes(
      args.outputPreviewBytes,
    );
  }

  override tools(): Tool<any>[] {
    const session = requireBoundSession(this.type, this._session);
    if (!session.execForeground) {
      throw new UserError(
        'Shell sandbox sessions must provide execForeground().',
      );
    }

    const tools: Tool<any>[] = [
      tool({
        name: 'Bash',
        description:
          'Executes a foreground bash command in the sandbox workspace and returns its output.',
        parameters: z.object({
          command: z.string().min(1).describe('The command to execute.'),
          timeout: z
            .number()
            .int()
            .min(1)
            .max(MAX_BASH_TIMEOUT_MS)
            .default(DEFAULT_BASH_TIMEOUT_MS)
            .describe(
              `Optional timeout in milliseconds. Defaults to ${DEFAULT_BASH_TIMEOUT_MS}; maximum is ${MAX_BASH_TIMEOUT_MS}.`,
            ),
          description: z
            .string()
            .optional()
            .describe(
              'A short description of what this command does, usually 5-10 words.',
            ),
        }),
        execute: async (
          {
            command,
            timeout,
            description,
          }: {
            command: string;
            timeout: number;
            description?: string;
          },
          _context?: unknown,
          details?: ToolCallDetails,
        ): Promise<string> => {
          assertForegroundBashCommand(command);
          return await withSandboxSpan(
            'sandbox.bash',
            {
              cmd: command,
              timeout_ms: timeout,
              description,
              run_as: this._runAs,
            },
            async () => {
              const result = await session.execForeground!({
                cmd: command,
                shell: '/bin/bash',
                login: true,
                timeoutMs: timeout,
                outputPreviewBytes: this.outputPreviewBytes,
                outputId: details?.toolCall?.callId,
                runAs: this._runAs,
              } satisfies ExecForegroundArgs);
              return formatExecResponse(result);
            },
          );
        },
      }),
    ];

    return this.configureTools ? this.configureTools([...tools]) : tools;
  }

  override instructions(): string {
    return SHELL_INSTRUCTIONS;
  }
}

export type Shell = ShellCapability;

export function shell(args: ShellArgs = {}): Shell {
  return new ShellCapability(args);
}

function assertForegroundBashCommand(command: string): void {
  if (hasUnquotedBackgroundOperator(command)) {
    throw new UserError(
      'Bash only supports foreground commands; remove background operator `&`.',
    );
  }

  const detachedCommand = findDetachedCommand(command);
  if (detachedCommand) {
    throw new UserError(
      `Bash only supports foreground commands; do not use \`${detachedCommand}\`.`,
    );
  }
}

function hasUnquotedBackgroundOperator(command: string): boolean {
  let quote: "'" | '"' | undefined;
  let escaped = false;

  for (let index = 0; index < command.length; index += 1) {
    const char = command[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === '\\' && quote !== "'") {
      escaped = true;
      continue;
    }
    if (quote) {
      if (char === quote) {
        quote = undefined;
      }
      continue;
    }
    if (char === "'" || char === '"') {
      quote = char;
      continue;
    }
    if (char !== '&') {
      continue;
    }

    const previous = previousNonWhitespace(command, index);
    const next = command[index + 1];
    if (next === '&' || command[index - 1] === '&') {
      continue;
    }
    if (previous === '>' || previous === '<') {
      continue;
    }
    return true;
  }

  return false;
}

function findDetachedCommand(command: string): string | undefined {
  for (const segment of splitShellCommandSegments(command)) {
    const commandName = firstCommandWord(segment);
    if (commandName && DETACHED_COMMANDS.has(commandName)) {
      return commandName;
    }
  }
  return undefined;
}

function splitShellCommandSegments(command: string): string[] {
  const segments: string[] = [];
  let start = 0;
  let quote: "'" | '"' | undefined;
  let escaped = false;

  for (let index = 0; index < command.length; index += 1) {
    const char = command[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === '\\' && quote !== "'") {
      escaped = true;
      continue;
    }
    if (quote) {
      if (char === quote) {
        quote = undefined;
      }
      continue;
    }
    if (char === "'" || char === '"') {
      quote = char;
      continue;
    }
    if (char === '\n' || char === ';' || char === '|' || char === '&') {
      segments.push(command.slice(start, index));
      if ((char === '|' || char === '&') && command[index + 1] === char) {
        index += 1;
      }
      start = index + 1;
    }
  }

  segments.push(command.slice(start));
  return segments;
}

function firstCommandWord(segment: string): string | undefined {
  const words = segment.trim().split(/\s+/u).filter(Boolean);
  for (const word of words) {
    if (/^[A-Za-z_][A-Za-z0-9_]*=.*/u.test(word)) {
      continue;
    }
    return stripSimpleQuotes(word);
  }
  return undefined;
}

function previousNonWhitespace(value: string, beforeIndex: number): string {
  for (let index = beforeIndex - 1; index >= 0; index -= 1) {
    const char = value[index];
    if (!/\s/u.test(char)) {
      return char;
    }
  }
  return '';
}

function stripSimpleQuotes(value: string): string {
  if (
    value.length >= 2 &&
    ((value.startsWith("'") && value.endsWith("'")) ||
      (value.startsWith('"') && value.endsWith('"')))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

function normalizeOutputPreviewBytes(value?: number): number {
  if (value == null) {
    return DEFAULT_BASH_OUTPUT_PREVIEW_BYTES;
  }
  if (!Number.isFinite(value) || value <= 0) {
    return DEFAULT_BASH_OUTPUT_PREVIEW_BYTES;
  }
  return Math.min(Math.floor(value), MAX_BASH_OUTPUT_PREVIEW_BYTES);
}
